import {
  LayoutDashboard,
  FolderKanban,
  Activity,
  HeartPulse,
  Settings,
  MapPin,
  FileText,
  FileBarChart,
  Truck,
  ClipboardCheck,
  BarChart3,
  Wrench,
  Radar,
  Boxes,
  Presentation,
  ShieldCheck,
  Network,
  Package,
} from "lucide-react";
import type { NavItem, NavItemStatus } from "./adminNav";

// ---------------------------------------------------------------------------
// Frontier status table (#383)
//
// One source of truth for what each Frontier surface is, mirroring the Command
// table from #379. The static nav below declares the same status inline (a
// contract test keeps the two in step) and `useShellNav` re-asserts it over any
// persisted menu-config payload, so a stored override cannot put an unverified
// stage back in front of an operator.
//
// The verified journey is Inicio → Proyectos. Monitorización and Garage
// Health are usable but unfinished; GEO went (QA-11): it repeated each
// project's planner, and its route now opens it. Ajustes and the project stages past
// «Revisar y validar» stay mounted for deep links but leave the menu.
// ---------------------------------------------------------------------------

/** Superseded or not part of the verified journey — mounted, off the menu. */
const FRONTIER_LEGACY_PREFIXES = ["/ops/settings", "/ops/geo"];

/** Usable but unfinished — offered with a Beta marker. */
const FRONTIER_BETA_PREFIXES = [
  "/ops/garage",
  "/ops/analytics",
  "/ops/monitoring",
  "/ops/garage-health",
  "/ops/release",
];

/**
 * Project workspace tabs kept mounted for deep links but hidden from the
 * project sidebar unless the build sets `VITE_SHOW_LEGACY_NAV=true`.
 */
export const FRONTIER_HIDDEN_PROJECT_TABS = ["maintenance"] as const;

/**
 * Project stages still to be rebuilt: shown in order, greyed, with no link
 * (their routes stay mounted for old links). Maintenance goes with «Operar».
 */
export const FRONTIER_PLANNED_PROJECT_TABS = [
  "installation",
  "commissioning",
  "production",
] as const;

/** Project workspace tabs shown only while a session feature flag is on. */
const FRONTIER_PROJECT_TAB_FLAGS: Record<string, string> = {
  area: "geotron_enabled",
};

/** The feature flag a Frontier href needs, or `undefined` when it needs none. */
export function frontierNavFeatureFlag(href: string): string | undefined {
  const tab = frontierProjectTabSlug(href);
  return tab === null ? undefined : FRONTIER_PROJECT_TAB_FLAGS[tab];
}

/** Project workspace tabs that are offered, but not yet accepted. */
const FRONTIER_BETA_PROJECT_TABS = ["formulation", "client", "area", "spatial"];

function matchesRoutePrefix(href: string, prefix: string): boolean {
  return href === prefix || href.startsWith(`${prefix}/`);
}

/**
 * The project workspace tab a href addresses, or `null` when it addresses
 * something else. Accepts both the relative hrefs the project sidebar uses
 * (`"planning"`) and a full workspace path (`/ops/projects/<id>/planning`), so
 * the same table answers for the menu and for a deep link.
 */
export function frontierProjectTabSlug(href: string): string | null {
  if (!href.startsWith("/")) {
    return href.split("/")[0] || null;
  }
  const match = href.match(/^\/ops\/projects\/[^/]+\/([^/]+)/);
  return match?.[1] ?? null;
}

/** Canonical status for a project workspace tab. */
export function frontierProjectTabStatus(
  tab: string,
): NavItemStatus | undefined {
  if ((FRONTIER_HIDDEN_PROJECT_TABS as readonly string[]).includes(tab)) {
    return "legacy";
  }
  if ((FRONTIER_PLANNED_PROJECT_TABS as readonly string[]).includes(tab)) {
    return "planned";
  }
  if (FRONTIER_BETA_PROJECT_TABS.includes(tab)) {
    return "beta";
  }
  return undefined;
}

/** True when a workspace path lands on a stage that left the project sidebar. */
export function isHiddenFrontierProjectTab(pathname: string): boolean {
  const tab = frontierProjectTabSlug(pathname);
  return tab !== null && frontierProjectTabStatus(tab) === "legacy";
}

/**
 * Canonical status for a Frontier href, or `undefined` when the surface is
 * verified (i.e. live). Prefix-scoped so sub-routes inherit the verdict of
 * their section, and boundary-aware so `/ops/settings-export` is not mistaken
 * for `/ops/settings`.
 */
export function frontierNavStatus(href: string): NavItemStatus | undefined {
  const tab = frontierProjectTabSlug(href);
  if (tab !== null) {
    return frontierProjectTabStatus(tab);
  }
  if (
    FRONTIER_BETA_PREFIXES.some((prefix) => matchesRoutePrefix(href, prefix))
  ) {
    return "beta";
  }
  if (
    FRONTIER_LEGACY_PREFIXES.some((prefix) => matchesRoutePrefix(href, prefix))
  ) {
    return "legacy";
  }
  return undefined;
}

export const opsMainNav: NavItem[] = [
  {
    name: "Dashboard",
    labelKey: "nav.dashboard",
    href: "/ops/dashboard",
    icon: LayoutDashboard,
  },
  {
    name: "Proyectos",
    labelKey: "nav.projects",
    href: "/ops/projects",
    icon: FolderKanban,
    matchPrefix: "/ops/projects/",
    // The project menu (Jorge, 27-oct): the summary, then the stages in
    // order, then the tools. Area intelligence is no step: it is a panel of
    // the planner, and `area` stays mounted for old links.
    children: [
      {
        name: "Resumen",
        labelKey: "nav.summary",
        href: "summary",
        icon: FileText,
      },
      {
        name: "Planificar",
        labelKey: "nav.planning",
        href: "planning",
        icon: MapPin,
        group: "stages",
      },
      {
        name: "Revisar y validar",
        labelKey: "nav.formulation",
        href: "formulation",
        icon: Boxes,
        status: "beta",
        group: "stages",
      },
      // The legacy ThingsBoard twin tab is retired (G-g): gate 4 «Twin
      // activation» is a «Coming soon» step of the rail, and `twin` redirects
      // to the summary.
      {
        name: "Instalar",
        labelKey: "nav.installation",
        href: "installation",
        icon: Truck,
        status: "planned",
        group: "stages",
      },
      {
        name: "Poner en marcha",
        labelKey: "nav.stage.commission",
        href: "commissioning",
        icon: ClipboardCheck,
        status: "planned",
        group: "stages",
      },
      {
        name: "Operar",
        labelKey: "nav.stage.operate",
        href: "production",
        icon: BarChart3,
        status: "planned",
        group: "stages",
      },
      // Kodiak publication — SLICE-019 (#423): the routed "Vista cliente" tab.
      {
        name: "Vista cliente",
        labelKey: "nav.clientView",
        href: "client",
        // Registered in iconRegistry so a stored menu override keeps it.
        icon: Presentation,
        status: "beta",
        group: "tools",
      },
      // Kodiak UIFO cases: Frontier execution surface of the case journey.
      {
        name: "Vigilancia",
        labelKey: "nav.vigilancia",
        href: "vigilancia",
        icon: ShieldCheck,
        group: "tools",
      },
      {
        name: "Correlación espacial",
        labelKey: "nav.spatialCorrelation",
        href: "spatial",
        icon: Radar,
        status: "beta",
        group: "tools",
      },
      // === Hidden unless VITE_SHOW_LEGACY_NAV — routes stay mounted (#383) ===
      {
        name: "Mantenimiento",
        labelKey: "nav.maintenance",
        href: "maintenance",
        icon: Wrench,
        status: "legacy",
        group: "stages",
      },
    ],
  },
  {
    // Garage of product units (decision N): Factory handoffs approved with
    // four eyes, and the units Core holds. Not tied to a project.
    name: "Garage",
    labelKey: "nav.garage",
    href: "/ops/garage",
    icon: Package,
    matchPrefix: "/ops/garage/",
    status: "beta",
  },
  {
    name: "Garage Health",
    labelKey: "nav.garageHealth",
    href: "/ops/garage-health",
    icon: HeartPulse,
    matchPrefix: "/ops/garage-health",
    status: "beta",
    children: [
      {
        name: "Report",
        labelKey: "nav.report",
        href: "/ops/garage-health/report",
        icon: FileBarChart,
        status: "beta",
      },
    ],
  },
  {
    name: "Release",
    labelKey: "nav.release",
    href: "/ops/release",
    icon: Network,
    matchPrefix: "/ops/release",
    status: "beta",
  },
  {
    name: "Monitoring",
    labelKey: "nav.monitoring",
    href: "/ops/monitoring",
    icon: Activity,
    status: "beta",
  },
  {
    name: "Analytics",
    labelKey: "nav.analytics",
    href: "/ops/analytics",
    icon: BarChart3,
    status: "beta",
  },
];

export const opsBottomNav: NavItem[] = [
  {
    name: "Settings",
    labelKey: "nav.settings",
    href: "/ops/settings",
    icon: Settings,
    status: "legacy",
  },
];
